// GRAVY-10814 — the Review modal's time fields show a TIME, not a datetime.
//
// The Figma (39621:199579) renders each side as "6:00 PM" / "12:29 AM" in a
// 120px box, so the field displays only the clock time while the value it edits
// is still a full datetime. These helpers are that translation, kept apart from
// the component so the parsing rules are testable on their own.
//
// Values are the `datetime-local` strings the modal already holds
// (`YYYY-MM-DDTHH:mm`), so nothing downstream — `netHours`,
// `timesheetEditError`, the Approve payload — has to change.

const pad2 = (n: number) => String(n).padStart(2, '0');

/** `2026-08-13T18:00` -> `6:00 PM`. Empty for an unparseable value. */
export const formatTimeOfDay = (value?: string | null): string => {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  const h24 = at.getHours();
  const ap = h24 < 12 ? 'AM' : 'PM';
  return `${h24 % 12 || 12}:${pad2(at.getMinutes())} ${ap}`;
};

/**
 * A typed time -> minutes since midnight, or null when it cannot be read.
 *
 * Accepts `6`, `6:07`, `6:07 am`, `6 PM`, `18:30`, with loose casing and
 * spacing — the same latitude as the Order v2 schedule field, because admins
 * type the same shapes.
 *
 * MINUTE PRECISION, deliberately. The sibling helper in
 * `CreateOrderV2/Steps/ScheduleStep/timePicker.ts` snaps to a 15-minute grid,
 * which is right for scheduling a shift and wrong here: these are recorded
 * clock-ins, the design itself shows 12:29 AM / 5:01 PM / 7:59 AM, and rounding
 * them would quietly rewrite the hours ops is being asked to approve.
 */
export const parseTimeOfDay = (input: string): number | null => {
  const m = input
    .trim()
    .toUpperCase()
    .match(/^(\d{1,2})(?::(\d{1,2}))?\s*(AM|PM|A|P)?$/);
  if (!m) return null;

  let h = parseInt(m[1], 10);
  const mins = m[2] != null ? parseInt(m[2], 10) : 0;
  const ap = m[3] ? m[3][0] : null;

  if (mins > 59) return null;

  if (ap) {
    if (h < 1 || h > 12) return null;
    if (ap === 'P' && h !== 12) h += 12;
    if (ap === 'A' && h === 12) h = 0;
  } else if (h > 23) {
    return null;
  }

  return h * 60 + mins;
};

/**
 * Puts a new time onto the DATE the value already carries.
 *
 * Keeping the date is the whole reason this is not a bare time input: a shift
 * running 6:00 PM to 12:29 AM has a check-out on the FOLLOWING day, and the
 * design shows that check-out as "12:29 AM" with nothing on screen naming its
 * date. Rebuilding from the field's own value preserves whichever day it was
 * on, so editing the minutes cannot silently move a post-midnight check-out
 * back to the shift's start date.
 *
 * The date is therefore not editable from this control — see the component,
 * which keeps the full moment on hover so it is never hidden, only fixed.
 */
export const applyTimeOfDay = (
  value: string,
  minutesFromMidnight: number,
): string | null => {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  const h = Math.floor(minutesFromMidnight / 60);
  const m = minutesFromMidnight % 60;
  return `${at.getFullYear()}-${pad2(at.getMonth() + 1)}-${pad2(at.getDate())}T${pad2(h)}:${pad2(m)}`;
};

/** "Aug 13, 6:00 PM" — the date the time-only field cannot show. */
export const describeMoment = (value?: string | null): string | undefined => {
  if (!value) return undefined;
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return undefined;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(at);
};

// ---------------------------------------------------------------------------
// GRAVY-10818 — the DAY half.
//
// The field above shows a time only, which was right for the Figma and wrong
// for the industry: an overnight shift's check-out lands on the following
// calendar day, and with no way to say so, "10:00 PM to 6:00 AM" was
// indistinguishable from a check-out entered before its check-in — and got
// blocked by the GRAVY-10812 range validation as if it were a mistake.
//
// So the date stays part of the value (it always was) and becomes adjustable,
// rather than fixed to whatever the server sent.
// ---------------------------------------------------------------------------

/** Same value, moved whole days. `+1` for an overnight check-out. */
export const shiftDays = (value: string, days: number): string | null => {
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return null;
  at.setDate(at.getDate() + days);
  return `${at.getFullYear()}-${pad2(at.getMonth() + 1)}-${pad2(at.getDate())}T${pad2(at.getHours())}:${pad2(at.getMinutes())}`;
};

/** "Fri, Aug 14" — the label on the picker's date stepper. */
export const formatDayLabel = (value?: string | null): string => {
  if (!value) return '';
  const at = new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(at);
};

/**
 * Whole days from `from` to `to`, comparing CALENDAR dates rather than
 * subtracting timestamps — 11:00 PM to 1:00 AM is one day apart even though
 * only two hours separate them, and that is the number the "+1 day" badge
 * needs. Returns null when either side is unreadable.
 */
export const dayOffset = (
  from?: string | null,
  to?: string | null,
): number | null => {
  if (!from || !to) return null;
  const a = new Date(from);
  const b = new Date(to);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  const dayA = new Date(a.getFullYear(), a.getMonth(), a.getDate()).getTime();
  const dayB = new Date(b.getFullYear(), b.getMonth(), b.getDate()).getTime();
  return Math.round((dayB - dayA) / 86_400_000);
};

/**
 * "+1 day" / "-1 day", or null when the two sides share a date.
 *
 * Rendered beside the check-out so an overnight shift is legible at a glance.
 * Without it the modal shows "10:00 PM" and "6:00 AM" with nothing saying they
 * are a day apart, which reads like an eight-hour error rather than an
 * eight-hour shift.
 */
export const dayOffsetLabel = (
  anchor?: string | null,
  value?: string | null,
): string | null => {
  const offset = dayOffset(anchor, value);
  if (offset === null || offset === 0) return null;
  const magnitude =
    Math.abs(offset) === 1 ? '1 day' : `${Math.abs(offset)} days`;
  return offset > 0 ? `+${magnitude}` : `-${magnitude}`;
};

/**
 * Times the picker offers, on a 15-minute grid (96 slots, 12:00 AM -> 11:45 PM).
 *
 * A LIST is a convenience, not the precision contract: typing into the field
 * still keeps the exact minute (GRAVY-10814), because recorded clock-ins are
 * things like 5:01 PM and must not be rounded behind anyone's back. Picking
 * 6:00 AM from a list is the admin choosing that value outright, which is a
 * different act from silently snapping what they typed.
 */
export const TIME_CHOICES: string[] = Array.from({ length: 96 }, (_, i) => {
  const h24 = Math.floor((i * 15) / 60);
  const mins = (i * 15) % 60;
  return `${h24 % 12 || 12}:${pad2(mins)} ${h24 < 12 ? 'AM' : 'PM'}`;
});
